import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, LogOut, ChevronRight, CheckCircle2, Clock, FileText, Landmark, User, Home, Camera, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import ElevareMark from "./ElevareMark";
import ProfilePhotoCropModal from "./ProfilePhotoCropModal";

/**
 * New-hire onboarding gate. Non-founder staff MUST complete this before any
 * admin tab unlocks. Backend enforces re-check on every admin request via
 * /api/admin-auth/onboarding (returns { needs_onboarding, status }).
 *
 * Renders one of three states:
 *   - "loading"     — checking status on mount
 *   - "form"        — profile still incomplete, show the multi-step form
 *   - "submitted"   — waiting on Kalib/John to finalize
 */

const LEGAL_DOCS = [
  { key: "employment_agreement",
    title: "Employment Agreement",
    body: "I confirm I have read the Elevare Solutions employment / independent-contractor agreement and agree to its terms including scope of work, compensation schedule, expectations of professionalism, and confidentiality of all client data." },
  { key: "form_w9",
    title: "IRS Form W-9",
    body: "I certify that the taxpayer identification number and address provided above are accurate. I understand Elevare will use this information to issue tax documents (1099-NEC) at year-end and that providing false information is punishable under federal law." },
  { key: "form_i9",
    title: "Form I-9 · Employment Eligibility",
    body: "I attest, under penalty of perjury, that I am a US citizen, lawful permanent resident, or an alien authorized to work in the United States. I will provide supporting identity + work-authorization documents to a founder upon request." },
  { key: "direct_deposit_auth",
    title: "Direct Deposit Authorization",
    body: "I authorize Elevare Solutions to deposit my pay into the bank account entered above via ACH. I understand this authorization remains in effect until I revoke it in writing, and that I am responsible for keeping this information current." },
  { key: "confidentiality",
    title: "Confidentiality & IP Assignment",
    body: "I agree to keep all client, financial, and internal Elevare information strictly confidential during and after my engagement. I assign to Elevare all work product, code, documents, and materials I create in the scope of my work." },
];

const STEPS = [
  { id: "personal", label: "Personal", icon: User },
  { id: "address", label: "Address", icon: Home },
  { id: "bank", label: "Direct deposit", icon: Landmark },
  { id: "legal", label: "Legal docs", icon: FileText },
  { id: "review", label: "Review & sign", icon: ShieldCheck },
];

export default function StaffOnboarding({ user, onLogout, onCompleted }) {
  const [status, setStatus] = useState("loading"); // loading|form|submitted
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [profile, setProfile] = useState({
    legal_name: user?.display_name || "",
    phone: "",
    email: user?.email || "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    ssn_last4: "",
    dob: "",
    bank_name: "",
    bank_routing: "",
    bank_account: "",
    bank_account_type: "checking",
  });

  const [signedDocs, setSignedDocs] = useState(() =>
    LEGAL_DOCS.reduce((acc, d) => ({ ...acc, [d.key]: { agreed: false, signed_name: "" } }), {})
  );
  const [typedSignature, setTypedSignature] = useState("");

  // ----- Cover-page photo (crop + upload, same widget founders use) -------
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const photoFileRef = useRef(null);

  useEffect(() => {
    api.get("/admin-auth/me")
      .then(({ data }) => setPhotoUrl(data?.photo_data_url || null))
      .catch(() => {});
  }, []);

  const handlePhotoPicked = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file."); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Image too large (20MB max)."); return; }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.onerror = () => toast.error("Could not read that image.");
    reader.readAsDataURL(file);
    if (photoFileRef.current) photoFileRef.current.value = "";
  };

  const handleCropConfirm = async (croppedFile) => {
    setPhotoBusy(true);
    try {
      const form = new FormData();
      form.append("file", croppedFile);
      const { data } = await api.post("/admin-auth/me/photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(data?.photo_data_url || null);
      toast.success("Photo saved — it appears on every PDF cover from day one.");
      setCropSrc(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      await api.delete("/admin-auth/me/photo");
      setPhotoUrl(null);
      toast.success("Photo removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove.");
    } finally {
      setPhotoBusy(false);
    }
  };

  // Bootstrap: check server status; short-circuit to `submitted` if already sent.
  useEffect(() => {
    api.get("/admin-auth/onboarding")
      .then(({ data }) => {
        if (!data?.needs_onboarding) {
          onCompleted?.();
          return;
        }
        if ((data?.status || "incomplete") === "submitted") setStatus("submitted");
        else setStatus("form");
      })
      .catch(() => setStatus("form"));
    // eslint-disable-next-line
  }, []);

  const setField = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const toggleDoc = (key) => setSignedDocs((s) => ({
    ...s,
    [key]: { ...s[key], agreed: !s[key].agreed },
  }));
  const setDocSignedName = (key, v) => setSignedDocs((s) => ({
    ...s,
    [key]: { ...s[key], signed_name: v },
  }));

  // ----- Per-step validation ----------------------------------------------
  const stepValid = useMemo(() => {
    if (step === 0) {
      const okPhone = (profile.phone || "").replace(/\D+/g, "").length >= 10;
      const okEmail = /^\S+@\S+\.\S+$/.test(profile.email || "");
      const okSsn = !profile.ssn_last4 || /^\d{4}$/.test(profile.ssn_last4);
      return profile.legal_name.trim().length >= 2 && okPhone && okEmail && okSsn;
    }
    if (step === 1) {
      return profile.address_line1.trim().length >= 3
        && profile.city.trim().length >= 2
        && profile.state.trim().length >= 2
        && profile.postal_code.trim().length >= 3;
    }
    if (step === 2) {
      return profile.bank_name.trim().length >= 2
        && /^\d{9}$/.test((profile.bank_routing || "").trim())
        && (profile.bank_account || "").trim().replace(/-/g, "").match(/^\d{4,17}$/);
    }
    if (step === 3) return LEGAL_DOCS.every((d) => signedDocs[d.key]?.agreed);
    if (step === 4) return (typedSignature || "").trim().length >= 2;
    return false;
  }, [step, profile, signedDocs, typedSignature]);

  const submit = async () => {
    if (!stepValid) return;
    setSaving(true);
    try {
      // Auto-populate any empty per-doc signature line with the master signature.
      const finalDocs = Object.fromEntries(
        LEGAL_DOCS.map((d) => [
          d.key,
          {
            agreed: !!signedDocs[d.key]?.agreed,
            signed_name: (signedDocs[d.key]?.signed_name || "").trim() || typedSignature.trim(),
          },
        ])
      );
      await api.post("/admin-auth/onboarding", {
        profile: {
          ...profile,
          bank_routing: profile.bank_routing.trim(),
          bank_account: profile.bank_account.trim(),
          ssn_last4: (profile.ssn_last4 || "").trim() || null,
          dob: (profile.dob || "").trim() || null,
          address_line2: (profile.address_line2 || "").trim() || null,
        },
        signed_docs: finalDocs,
        typed_signature: typedSignature.trim(),
      });
      toast.success("Onboarding submitted — waiting on founder review.");
      setStatus("submitted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit onboarding.");
    } finally {
      setSaving(false);
    }
  };

  const doLogout = async () => {
    try { await api.post("/admin-auth/logout"); } catch (_) {}
    onLogout?.();
  };

  // ------------------------------------------------------------------ shell
  return (
    <div className="min-h-screen bg-[#eae7de] text-[#1e3d3a]">
      <header className="border-b border-[#1e3d3a]/10 bg-white/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ElevareMark size={44} />
            <span className="font-head text-lg text-[#1e3d3a]">Elevare</span>
            <span className="uppercase tracking-[0.28em] text-[11px] text-[#c9a961]">New hire</span>
          </div>
          <button
            type="button"
            data-testid="onboarding-logout"
            onClick={doLogout}
            className="text-xs uppercase tracking-[0.25em] text-[#1e3d3a]/60 hover:text-[#1e3d3a] transition-colors duration-300 inline-flex items-center gap-2"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 lg:px-10 py-10">
        {status === "loading" && (
          <div className="flex items-center justify-center py-24" data-testid="onboarding-loading">
            <Loader2 className="h-6 w-6 animate-spin text-[#c9a961]" />
          </div>
        )}

        {status === "submitted" && (
          <div data-testid="onboarding-submitted" className="rounded-3xl border border-[#c9a961]/40 bg-white p-10 text-center shadow-[0_2px_0_rgba(30,61,58,0.04)]">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#c9a961]/15 flex items-center justify-center mb-4">
              <Clock className="h-6 w-6 text-[#a3823f]" />
            </div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#a3823f]">Elevare · Onboarding</p>
            <h1 className="font-head text-3xl mt-2 font-light">Awaiting founder review</h1>
            <p className="text-sm text-[#1e3d3a]/65 mt-3 max-w-md mx-auto leading-relaxed">
              Your profile, bank details, and signed documents have been sent to Kalib and John.
              You&rsquo;ll get an email the moment your access is unlocked.
              Feel free to close this tab.
            </p>
            <button
              type="button"
              onClick={doLogout}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-6 py-3 text-xs uppercase tracking-[0.24em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        )}

        {status === "form" && (
          <div data-testid="onboarding-form" className="space-y-6">
            {/* Header + progress rail */}
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#c9a961]">Welcome, {user?.display_name || user?.username}</p>
              <h1 className="font-head text-4xl sm:text-5xl mt-2 font-light leading-tight">
                Let&rsquo;s finish your <span className="text-[#a3823f] italic">onboarding</span>.
              </h1>
              <p className="text-sm text-[#1e3d3a]/60 mt-3 max-w-2xl leading-relaxed">
                Before you get access to leads, clients, and the CRM, we need your personal info,
                direct-deposit banking, and signatures on the standard new-hire documents.
                Kalib or John will unlock your access as soon as they&rsquo;ve reviewed it.
              </p>
            </div>

            {/* Step indicator */}
            <ol data-testid="onboarding-steps" className="flex items-center gap-1 md:gap-3 text-[10px] uppercase tracking-[0.2em]">
              {STEPS.map((s, i) => {
                const active = i === step, done = i < step;
                return (
                  <li key={s.id} className="flex items-center gap-1 md:gap-3">
                    <button
                      type="button"
                      disabled={i > step}
                      onClick={() => i < step && setStep(i)}
                      className={`flex items-center gap-2 rounded-full border px-3 py-1.5 transition-colors duration-300 ${
                        active ? "bg-[#1e3d3a] text-white border-[#1e3d3a]"
                          : done ? "border-[#c9a961]/60 text-[#a3823f] bg-white"
                          : "border-[#1e3d3a]/12 text-[#1e3d3a]/40 bg-white/60"
                      }`}
                    >
                      <s.icon className="h-3 w-3" />
                      <span className="hidden md:inline">{s.label}</span>
                    </button>
                    {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-[#1e3d3a]/25" />}
                  </li>
                );
              })}
            </ol>

            {/* Step panels */}
            <section className="rounded-3xl border border-[#1e3d3a]/12 bg-white p-6 lg:p-8 shadow-[0_2px_0_rgba(30,61,58,0.04)]">
              {step === 0 && (
                <div data-testid="step-personal" className="space-y-4">
                  <h2 className="font-head text-2xl font-light">Personal information</h2>
                  <p className="text-sm text-[#1e3d3a]/60">This is what appears on your paycheck and tax forms.</p>

                  {/* Cover-page photo */}
                  <div data-testid="onb-photo-block" className="flex items-center gap-4 rounded-xl border border-[#c9a961]/35 bg-[#c9a961]/6 p-4">
                    <div className="relative shrink-0">
                      {photoUrl ? (
                        <img
                          data-testid="onb-photo-preview"
                          src={photoUrl}
                          alt="Cover page headshot"
                          className="h-16 w-16 rounded-full object-cover ring-2 ring-[#c9a961]/60"
                        />
                      ) : (
                        <div
                          data-testid="onb-photo-placeholder"
                          className="h-16 w-16 rounded-full bg-[#1e3d3a] text-[#c9a961] flex items-center justify-center font-head text-xl ring-2 ring-[#c9a961]/60"
                        >
                          {(user?.display_name || user?.username || "E").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <button
                        type="button"
                        data-testid="onb-photo-upload"
                        onClick={() => photoFileRef.current?.click()}
                        disabled={photoBusy}
                        title="Upload your cover-page photo"
                        className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-[#a3823f] text-white flex items-center justify-center hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60"
                      >
                        {photoBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                      </button>
                      <input
                        ref={photoFileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handlePhotoPicked(e.target.files?.[0])}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#1e3d3a]">Cover-page photo <span className="text-[#1e3d3a]/45 font-normal">(optional)</span></p>
                      <p className="text-[11px] text-[#1e3d3a]/60 leading-relaxed mt-0.5">
                        This headshot appears on every client-facing PDF cover you send. Upload it now
                        so your covers look complete on day one — you can adjust the framing before saving.
                      </p>
                      {photoUrl && (
                        <button
                          type="button"
                          data-testid="onb-photo-remove"
                          onClick={removePhoto}
                          disabled={photoBusy}
                          className="mt-1.5 text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45 hover:text-[#b23a48] inline-flex items-center gap-1 transition-colors duration-300"
                        >
                          <Trash2 className="h-2.5 w-2.5" /> Remove photo
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Legal full name" testid="onb-legal-name" value={profile.legal_name} onChange={(v)=>setField("legal_name",v)} />
                    <Field label="Phone" testid="onb-phone" value={profile.phone} onChange={(v)=>setField("phone",v)} placeholder="(555) 555-1234" />
                    <Field label="Email" testid="onb-email" value={profile.email} onChange={(v)=>setField("email",v)} placeholder="you@example.com" type="email" />
                    <Field label="Date of birth (optional)" testid="onb-dob" value={profile.dob} onChange={(v)=>setField("dob",v)} type="date" />
                    <Field label="SSN — last 4 (optional)" testid="onb-ssn4" value={profile.ssn_last4} onChange={(v)=>setField("ssn_last4",v.replace(/\D/g,"").slice(0,4))} placeholder="1234" maxLength={4} />
                  </div>
                </div>
              )}

              {step === 1 && (
                <div data-testid="step-address" className="space-y-4">
                  <h2 className="font-head text-2xl font-light">Mailing address</h2>
                  <p className="text-sm text-[#1e3d3a]/60">Used for tax documents and pay stubs.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Street address" testid="onb-addr1" value={profile.address_line1} onChange={(v)=>setField("address_line1",v)} className="md:col-span-2" />
                    <Field label="Apt / Suite (optional)" testid="onb-addr2" value={profile.address_line2} onChange={(v)=>setField("address_line2",v)} className="md:col-span-2" />
                    <Field label="City" testid="onb-city" value={profile.city} onChange={(v)=>setField("city",v)} />
                    <Field label="State" testid="onb-state" value={profile.state} onChange={(v)=>setField("state",v.toUpperCase().slice(0,2))} placeholder="TX" maxLength={2} />
                    <Field label="ZIP / Postal code" testid="onb-zip" value={profile.postal_code} onChange={(v)=>setField("postal_code",v)} placeholder="00000" />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="step-bank" className="space-y-4">
                  <h2 className="font-head text-2xl font-light">Direct deposit</h2>
                  <p className="text-sm text-[#1e3d3a]/60">Where should Elevare send your paychecks?</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Bank name" testid="onb-bank-name" value={profile.bank_name} onChange={(v)=>setField("bank_name",v)} placeholder="Chase, Wells Fargo, etc." className="md:col-span-2" />
                    <Field label="Routing number (9 digits)" testid="onb-routing" value={profile.bank_routing} onChange={(v)=>setField("bank_routing",v.replace(/\D/g,"").slice(0,9))} placeholder="026009593" />
                    <Field label="Account number" testid="onb-account" value={profile.bank_account} onChange={(v)=>setField("bank_account",v.replace(/[^0-9-]/g,"").slice(0,17))} placeholder="000123456789" />
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">Account type</label>
                      <select
                        data-testid="onb-acct-type"
                        value={profile.bank_account_type}
                        onChange={(e)=>setField("bank_account_type", e.target.value)}
                        className="w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de] px-4 py-3 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
                      >
                        <option value="checking">Checking</option>
                        <option value="savings">Savings</option>
                      </select>
                    </div>
                  </div>
                  <p className="text-[11px] text-[#1e3d3a]/50 mt-2">
                    Your full account number is encrypted at rest. Non-founders only ever see the last 4 digits.
                  </p>
                </div>
              )}

              {step === 3 && (
                <div data-testid="step-legal" className="space-y-5">
                  <h2 className="font-head text-2xl font-light">Standard new-hire documents</h2>
                  <p className="text-sm text-[#1e3d3a]/60">Read and check each — you&rsquo;ll counter-sign on the final step.</p>
                  <ul className="space-y-3">
                    {LEGAL_DOCS.map((d) => (
                      <li key={d.key} className="rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/50 p-4">
                        <label className="flex items-start gap-3 cursor-pointer" data-testid={`doc-${d.key}`}>
                          <input
                            type="checkbox"
                            data-testid={`doc-${d.key}-checkbox`}
                            checked={!!signedDocs[d.key]?.agreed}
                            onChange={() => toggleDoc(d.key)}
                            className="mt-1 h-4 w-4 accent-[#a3823f]"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-head text-lg text-[#1e3d3a]">{d.title}</p>
                              {signedDocs[d.key]?.agreed && <CheckCircle2 className="h-4 w-4 text-[#6b7c3a]" />}
                            </div>
                            <p className="text-sm text-[#1e3d3a]/65 leading-relaxed mt-1">{d.body}</p>
                          </div>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {step === 4 && (
                <div data-testid="step-review" className="space-y-5">
                  <h2 className="font-head text-2xl font-light">Review &amp; sign</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <SummaryRow label="Legal name" value={profile.legal_name} />
                    <SummaryRow label="Phone" value={profile.phone} />
                    <SummaryRow label="Email" value={profile.email} />
                    <SummaryRow label="DOB" value={profile.dob || "—"} />
                    <SummaryRow label="Address" value={`${profile.address_line1}${profile.address_line2 ? ", " + profile.address_line2 : ""}, ${profile.city}, ${profile.state} ${profile.postal_code}`} />
                    <SummaryRow label="Bank" value={`${profile.bank_name} · ${profile.bank_account_type}`} />
                    <SummaryRow label="Routing" value={profile.bank_routing} />
                    <SummaryRow label="Account (last 4)" value={`••••${profile.bank_account.slice(-4)}`} />
                    <SummaryRow label="Docs signed" value={`${Object.values(signedDocs).filter(v=>v.agreed).length} / ${LEGAL_DOCS.length}`} />
                  </div>

                  <div className="pt-4 border-t border-[#1e3d3a]/10">
                    <label className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">Type your full legal name to sign all documents</label>
                    <input
                      data-testid="onb-signature"
                      type="text"
                      value={typedSignature}
                      onChange={(e)=>setTypedSignature(e.target.value)}
                      placeholder={profile.legal_name || "Type your legal name"}
                      className="w-full rounded-xl border border-[#c9a961]/40 bg-white px-4 py-3.5 font-head text-2xl italic text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
                    />
                    <p className="text-[11px] text-[#1e3d3a]/50 mt-2">
                      By typing your name and clicking Submit, you affirm every document above as if hand-signed.
                    </p>
                  </div>
                </div>
              )}

              {/* Nav buttons */}
              <div className="flex items-center justify-between pt-6 mt-6 border-t border-[#1e3d3a]/10">
                <button
                  type="button"
                  disabled={step === 0}
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  className="text-xs uppercase tracking-[0.24em] text-[#1e3d3a]/50 hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-30"
                  data-testid="onb-back"
                >
                  Back
                </button>
                {step < STEPS.length - 1 ? (
                  <button
                    type="button"
                    disabled={!stepValid}
                    onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                    className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-6 py-3 text-xs uppercase tracking-[0.24em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-50"
                    data-testid="onb-next"
                  >
                    Continue <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={saving || !stepValid}
                    onClick={submit}
                    className="inline-flex items-center gap-2 rounded-full bg-[#a3823f] text-white px-8 py-3 text-xs uppercase tracking-[0.24em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60"
                    data-testid="onb-submit"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                    Submit for approval
                  </button>
                )}
              </div>
            </section>
          </div>
        )}
      </main>

      {cropSrc && (
        <ProfilePhotoCropModal
          imageSrc={cropSrc}
          busy={photoBusy}
          onConfirm={handleCropConfirm}
          onClose={() => setCropSrc(null)}
        />
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder, maxLength, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de] px-4 py-3 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
      />
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-[#1e3d3a]/10 py-2">
      <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50">{label}</span>
      <span className="text-sm text-[#1e3d3a] text-right truncate">{value || "—"}</span>
    </div>
  );
}
